<template>
    <div
        class="relative"
        :class="
            isCard &&
            'pt-10 px-8 lg:px-16 pb-11 lg:pb-14 text-black bg-white rounded-md border border-black border-opacity-10'
        "
    >
        <transition name="fade">
            <div
                v-if="isNewsletterSubscribeRequestSent"
                class="flex flex-col absolute -ml-1 w-[calc(100%+8px)] h-full top-0 left-0 py-4 px-0 lg:p-8 justify-center items-center text-center z-10"
                :class="isCard ? 'text-black bg-white' : 'text-white bg-blue-900'"
            >
                <i class="ri-checkbox-circle-fill text-4xl mb-2 text-green"></i>
                <span
                    class="mb-4 text-[1.5rem] lg:text-2xl font-Inter-Bold feedback-message-title"
                    :class="isCard && 'text-black'"
                >
                    {{ $t('newsletter.success_title') }}
                </span>
                <p class="feedback-message-subtitle">
                    {{ $t('newsletter.success_text') }}
                </p>
                <div @click="hideFeedbackMessage">
                    <PButton :label="$t('common.ok')" :appearance="isCard ? 'black' : 'secondary'" class="w-max mt-8" />
                </div>
            </div>
        </transition>
        <h3 class="font-Inter-Bold text-xl lg:text-2xl mb-5 text-white" :class="isCard && 'text-black'">
            {{ $t('newsletter.title') }}
        </h3>
        <p class="text-sm font-Inter-Regular leading-5 text-white" :class="isCard && 'text-black'">
            {{ $t('newsletter.description') }}
        </p>
        <ValidationObserver ref="form" slim>
            <form novalidate @submit.prevent="onSubmit">
                <div class="flex mt-6 gap-x-3 flex-wrap">
                    <div class="w-full lg:w-[calc(50%-0.375rem)]">
                        <ValidationProvider v-slot="{ errors }" vid="last_name" tag="div">
                            <Input
                                v-model="last_name"
                                has-icon
                                icon="user"
                                :error="errors"
                                :placeholder="$t('common.form.last_name_placeholder')"
                                :label="hasLabels ? $t('common.form.last_name') : null"
                                :required="false"
                                :is-error-visible="isErrorVisible"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="w-full lg:w-[calc(50%-0.375rem)] mt-4 lg:mt-0">
                        <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                            <Input
                                v-model="first_name"
                                has-icon
                                icon="user"
                                :error="errors"
                                :placeholder="$t('common.form.first_name_placeholder')"
                                :label="hasLabels ? $t('common.form.first_name') : null"
                                :required="false"
                                :is-error-visible="isErrorVisible"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="w-full mt-4 lg:mt-5">
                        <ValidationProvider v-slot="{ errors }" vid="email" tag="div">
                            <Input
                                v-model="email"
                                has-icon
                                icon="mail"
                                :error="errors"
                                :class="isCard ? 'text-black' : 'text-white'"
                                :placeholder="$t('newsletter.email_placeholder')"
                                :label="hasLabels ? $t('newsletter.email_label') : null"
                                :required="false"
                                :is-error-visible="isErrorVisible"
                            />
                            <div v-if="isSubscribed" class="text-left ml-auto mt-2 pl-4">
                                <span class="p-error text-red-100">{{ isSubscribed }}</span>
                            </div>
                        </ValidationProvider>
                        <ValidationProvider v-slot="{ errors }" vid="accept_privacy_policy" tag="div" class="mb-2">
                            <Input
                                :id="`gdpr-${index}`"
                                v-model="accept_privacy_policy"
                                class="mt-6 text-white"
                                is-checkbox
                                :error="errors"
                                :class="isCard ? 'text-black' : 'text-white'"
                                :label="$t('common.form.accept_privacy_policy')"
                                :is-error-visible="isErrorVisible"
                            />
                        </ValidationProvider>
                    </div>
                </div>
                <span
                    v-if="false"
                    class="text-xs pl-8 font-Inter-Regular opacity-80 leading-5"
                    :class="isCard ? 'text-black' : 'text-white'"
                    >This site is protected by <a class="font-Inter-Bold" href="/">reCAPTCHA</a> and the Google
                    <a class="font-Inter-Bold" href="/">Privacy Policy</a> and
                    <a class="font-Inter-Bold" href="/">Terms of Service</a> apply.</span
                >
                <div class="w-full flex justify-end lg:w-auto">
                    <div class="w-full lg:w-auto" @click="onSubmit">
                        <PButton
                            type="Button"
                            appearance="primary"
                            :label="$t('newsletter.subscribe')"
                            :has-preloader="isLoading"
                            class="w-full lg:w-auto lg:ml-auto mt-4 lg:mt-7 mb-4"
                        />
                    </div>
                </div>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
export default {
    name: 'NewsletterForm',
    props: {
        isCard: {
            type: Boolean,
            required: false,
            default: false,
        },
        index: {
            type: Number,
            required: false,
            default: 1,
        },
        hasLabels: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isNewsletterSubscribeRequestSent: false,
            isErrorVisible: false,
            last_name: '',
            first_name: '',
            email: '',
            isSubscribed: '',
            isLoading: false,
            accept_privacy_policy: false,
        };
    },
    async mounted() {
        try {
            await this.$recaptcha.init();
        } catch (e) {
            console.log(e);
        }
    },
    beforeDestroy() {
        this.$recaptcha.destroy();
    },
    methods: {
        async onSubmit() {
            this.isErrorVisible = true;
            this.isLoading = true;

            try {
                const token = await this.$recaptcha.execute('login');

                await this.$axios
                    .$post('/newsletter', {
                        first_name: this.first_name,
                        last_name: this.last_name,
                        email: this.email,
                        accept_privacy_policy: this.accept_privacy_policy ? 1 : 0,
                        reCaptcha: token,
                    })
                    .then((res) => {
                        this.isLoading = false;
                        if (res.success === true) {
                            this.isNewsletterSubscribeRequestSent = true;
                        } else {
                            this.$refs.form.setErrors(res.error);
                            this.isSubscribed = res.error?.subscribed;
                        }
                    });

                await this.$recaptcha.reset();
            } catch (err) {
                console.log(err);
            }
        },
        hideFeedbackMessage() {
            this.isNewsletterSubscribeRequestSent = false;
            this.first_name = '';
            this.last_name = '';
            this.email = '';
            this.isSubscribed = '';

            this.accept_privacy_policy = false;
        },
    },
};
</script>
